import {
  computed,
  inject,
  Injectable,
  resource,
  ResourceRef,
  Signal,
} from '@angular/core';
import {
  CONFIGURATION_SETTING_KEYS,
  EDirectSaleAlternatePassengerData,
  EMessageType,
  IFellowTravelerDto,
} from '@web/dtos';
import { IApiResponse } from '@web/shared-types';
import { firstValueFrom } from 'rxjs';
import { ConfigurationStore } from '../configuration/configuration-store.service';
import { FellowTravelerGateway } from './fellow-traveler.gateway';

@Injectable({
  providedIn: 'root',
})
export class FellowTravelerStore {
  private readonly configurationStore: ConfigurationStore =
    inject(ConfigurationStore);

  private readonly gateway: FellowTravelerGateway = new FellowTravelerGateway();

  readonly isActive: Signal<boolean> = computed((): boolean =>
    this.configurationStore.getBooleanConfiguration(
      CONFIGURATION_SETTING_KEYS.IsDirectSaleAlternatePassengerActive
    )
  );

  readonly isSalutationEnabled: Signal<boolean> = computed((): boolean =>
    this.configurationStore.hasMultiSelectConfigurationValue(
      CONFIGURATION_SETTING_KEYS.DirectSaleAlternatePassengerData,
      EDirectSaleAlternatePassengerData.Salutation
    )
  );

  readonly isDateOfBirthEnabled: Signal<boolean> = computed((): boolean =>
    this.configurationStore.hasMultiSelectConfigurationValue(
      CONFIGURATION_SETTING_KEYS.DirectSaleAlternatePassengerData,
      EDirectSaleAlternatePassengerData.DateOfBirth
    )
  );

  readonly #fellowTravelersResource: ResourceRef<
    IApiResponse<IFellowTravelerDto[]> | undefined
  > = resource({
    params: (): boolean | undefined => (this.isActive() ? true : undefined),
    loader: (): Promise<IApiResponse<IFellowTravelerDto[]>> =>
      firstValueFrom(this.gateway.list()),
  });

  readonly fellowTravelers: Signal<IFellowTravelerDto[]> = computed(
    (): IFellowTravelerDto[] =>
      this.#fellowTravelersResource.hasValue()
        ? (this.#fellowTravelersResource.value()?.data ?? [])
        : []
  );

  readonly isLoading: Signal<boolean> = computed((): boolean =>
    this.#fellowTravelersResource.isLoading()
  );

  reload(): void {
    this.#fellowTravelersResource.reload();
  }

  async create(
    query: IFellowTravelerDto
  ): Promise<IFellowTravelerDto | undefined> {
    const response: IApiResponse<IFellowTravelerDto> = await firstValueFrom(
      this.gateway.save(this.configuredQuery(query))
    );

    const createdFellowTraveler: IFellowTravelerDto | undefined = response.data;

    if (!createdFellowTraveler?.id) {
      return undefined;
    }

    this.#fellowTravelersResource.update(
      (
        currentResponse: IApiResponse<IFellowTravelerDto[]> | undefined
      ): IApiResponse<IFellowTravelerDto[]> => ({
        ...currentResponse,
        data: [...(currentResponse?.data ?? []), createdFellowTraveler],
      })
    );

    return createdFellowTraveler;
  }

  async update(
    fellowTravelerId: string,
    query: IFellowTravelerDto
  ): Promise<IFellowTravelerDto | undefined> {
    const response: IApiResponse<IFellowTravelerDto> = await firstValueFrom(
      this.gateway.update(fellowTravelerId, this.configuredQuery(query))
    );

    const updatedFellowTraveler: IFellowTravelerDto | undefined = response.data;

    if (!updatedFellowTraveler?.id) {
      return undefined;
    }

    this.#fellowTravelersResource.update(
      (
        currentResponse: IApiResponse<IFellowTravelerDto[]> | undefined
      ): IApiResponse<IFellowTravelerDto[]> => ({
        ...currentResponse,
        data: (currentResponse?.data ?? []).map(
          (fellowTraveler: IFellowTravelerDto): IFellowTravelerDto =>
            fellowTraveler.id === updatedFellowTraveler.id
              ? updatedFellowTraveler
              : fellowTraveler
        ),
      })
    );

    return updatedFellowTraveler;
  }

  async delete(fellowTravelerId: string): Promise<boolean> {
    const response: IApiResponse<IFellowTravelerDto[]> = await firstValueFrom(
      this.gateway.delete(fellowTravelerId)
    );

    if (
      response.message?.type !== EMessageType.SUCCESS ||
      response.data === undefined
    ) {
      return false;
    }

    const remainingFellowTravelers: IFellowTravelerDto[] = response.data;

    this.#fellowTravelersResource.update(
      (
        currentResponse: IApiResponse<IFellowTravelerDto[]> | undefined
      ): IApiResponse<IFellowTravelerDto[]> => ({
        ...currentResponse,
        data: remainingFellowTravelers,
      })
    );

    return true;
  }

  private configuredQuery(query: IFellowTravelerDto): IFellowTravelerDto {
    return {
      firstName: query.firstName,
      lastName: query.lastName,
      salutation: this.isSalutationEnabled()
        ? (query.salutation ?? null)
        : null,
      dateOfBirth: this.isDateOfBirthEnabled()
        ? (query.dateOfBirth ?? null)
        : null,
    };
  }
}
